import { Box, Input, Typography, Button, IconButton, Stack } from "@mui/joy";
import { useEffect, useState, useRef } from "react";
import { debounce } from "lodash";
import { TrendingUp, Plus, X } from "lucide-react";

interface IRoofSlopes {
    value: string | null;
    onChange: (value: string) => void;
    loading?: boolean;
}

interface SlopeEntry {
    section: string;
    pitch: string;
}

/**
 * Multi-slope entry component.
 * Stores as JSON array: [{"section":"Main","pitch":"6/12"},{"section":"Garage","pitch":"4/12"}]
 * Replaces the old single RoofPitch text input.
 */
export function RoofSlopes(props: IRoofSlopes) {
    const { value, onChange, loading } = props;

    const parseValue = (val: string | null): SlopeEntry[] => {
        if (!val) return [{ section: "", pitch: "" }];
        try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        } catch {
            // Legacy single value — migrate it
            if (val) return [{ section: "Main", pitch: val }];
        }
        return [{ section: "", pitch: "" }];
    };

    const [slopes, setSlopes] = useState<SlopeEntry[]>(() => parseValue(value));

    const debouncedOnChange = useRef(
        debounce((entries: SlopeEntry[]) => {
            const filtered = entries.filter((e) => e.pitch.trim() !== "");
            if (filtered.length > 0) {
                onChange(JSON.stringify(filtered));
            }
        }, 500),
    ).current;

    useEffect(() => {
        return () => { debouncedOnChange.cancel(); };
    }, [debouncedOnChange]);

    useEffect(() => {
        if (value) {
            setSlopes(parseValue(value));
        }
    }, [value]);

    const handleUpdate = (index: number, field: keyof SlopeEntry, val: string) => {
        if (loading) return;
        const updated = slopes.map((s, i) => (i === index ? { ...s, [field]: val } : s));
        setSlopes(updated);
        debouncedOnChange(updated);
    };

    const addSlope = () => {
        setSlopes((prev) => [...prev, { section: "", pitch: "" }]);
    };

    const removeSlope = (index: number) => {
        if (slopes.length <= 1) return;
        const updated = slopes.filter((_, i) => i !== index);
        setSlopes(updated);
        debouncedOnChange(updated);
    };

    return (
        <Box flex={1}>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>
                Roof Slopes
            </Typography>
            <Stack spacing={1}>
                {slopes.map((entry, i) => (
                    <Stack direction="row" spacing={1} key={i} alignItems="center">
                        <Input
                            size="sm"
                            placeholder="Section (e.g. Main)"
                            value={entry.section}
                            onChange={(e) => handleUpdate(i, "section", e.target.value)}
                            disabled={loading}
                            sx={{ flex: 1 }}
                        />
                        <Input
                            size="sm"
                            placeholder="Pitch (e.g. 6/12)"
                            value={entry.pitch}
                            onChange={(e) => handleUpdate(i, "pitch", e.target.value)}
                            disabled={loading}
                            startDecorator={<TrendingUp size={14} />}
                            sx={{ flex: 1 }}
                        />
                        {slopes.length > 1 && (
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={() => removeSlope(i)}
                                sx={{ "--IconButton-size": "28px" }}
                            >
                                <X size={14} />
                            </IconButton>
                        )}
                    </Stack>
                ))}
                <Button
                    size="sm"
                    variant="plain"
                    color="neutral"
                    startDecorator={<Plus size={14} />}
                    onClick={addSlope}
                    sx={{ alignSelf: "flex-start", mt: 0.5 }}
                >
                    Add Slope
                </Button>
            </Stack>
        </Box>
    );
}
